import React, { useRef, useState } from 'react';
import { postService } from '../../../services/post.service';
import { toast } from 'react-hot-toast';
import DialogComponent from '../../dialog/dialog.component';
import { Send, Smile } from 'lucide-react';
import EmojiPopover from '../../emoji-popover/emoji-popover';

interface EditCommentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  commentId: string;
  commentType: number;
  initialContent: string;
  onUpdate: (id: string, content: string) => void;
}

export const EditCommentDialog = ({
  isOpen,
  onClose,
  commentId,
  commentType,
  initialContent,
  onUpdate,
}: EditCommentDialogProps) => {
  const [content, setContent] = useState(initialContent);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiBtnRef = useRef<HTMLButtonElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) {
      toast.error('Nội dung bình luận không được để trống');
      return;
    }

    try {
      setIsSubmitting(true);
      await postService.editComment({
        id: commentId,
        commentType,
        content: content.trim(),
        imageId: null,
      });

      onUpdate(commentId, content.trim());
      toast.success('Đã cập nhật bình luận');
      onClose();
    } catch (error) {
      console.log(error);
      toast.error('Không thể cập nhật bình luận');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onEmojiSelect = (emoji: any) => {
    setContent((prev) => prev + emoji.native);
    setShowEmojiPicker(false);
  };

  return (
    <DialogComponent visible={isOpen} onClose={onClose} title="Chỉnh sửa bình luận" size="lg" placement="CENTER">
      <div className="mt-4">
        <form onSubmit={handleSubmit}>
          <div className="relative">
            <textarea
              className="w-full p-4 bg-gray-100 dark:bg-zinc-800 rounded-lg border-0 
                         text-slate-600 dark:text-slate-300 outline-none resize-none"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              disabled={isSubmitting}
              placeholder="Nhập nội dung bình luận..."
            />
            <button
              ref={emojiBtnRef}
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="absolute right-3 bottom-3"
            >
              <Smile size={20} className="text-slate-500" />
            </button>
            <EmojiPopover
              open={showEmojiPicker}
              anchorRef={emojiBtnRef}
              onEmojiSelect={onEmojiSelect}
              onClose={() => setShowEmojiPicker(false)}
            />
          </div>
          <div className="mt-4 flex justify-end space-x-2">
            <button
              type="button"
              className="px-4 py-2 rounded-md text-gray-600 dark:text-gray-300 
                         hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Hủy
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 
                         disabled:opacity-50 flex items-center gap-2"
              disabled={isSubmitting || !content.trim()}
            >
              <span>Lưu thay đổi</span>
              {!isSubmitting && <Send size={16} />}
            </button>
          </div>
        </form>
      </div>
    </DialogComponent>
  );
};
